import { Mutation, MutationCache } from '@tanstack/react-query';
import { recordAction } from './activityStore';

export function activityMeta(
  label: string,
  entity?: { type: string; id?: string; name?: string }
): { activity: string; entity?: { type: string; id?: string; name?: string } } {
  return { activity: label, entity };
}

/**
 * Creates a hardened TanStack Query MutationCache that correlates mutations
 * to the route path where they were initiated.
 *
 * Implements Ox Alpha Directive R2:
 * 1. Opt-in via meta.activity (explicitly false opts out).
 * 2. Success-only recording (optimistic rollbacks never produce phantom action nodes).
 * 3. Deduplication on mutation.mutationId.
 * 4. URL path captured at onMutate initiation time.
 */
export function createActivityMutationCache(getPath: () => string): MutationCache {
  const mutationStartPathMap = new WeakMap<Mutation<unknown, unknown, unknown, unknown>, string>();
  const processedMutationIds = new Set<number>();

  return new MutationCache({
    onMutate: (_variables, mutation) => {
      mutationStartPathMap.set(mutation, getPath());
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      const meta = mutation.meta;
      if (!meta || !meta.activity || meta.activity === false) {
        return;
      }

      if (mutation.mutationId && processedMutationIds.has(mutation.mutationId)) {
        return;
      }
      if (mutation.mutationId) {
        processedMutationIds.add(mutation.mutationId);
        // keep set bounded
        if (processedMutationIds.size > 200) {
          const first = processedMutationIds.values().next().value;
          if (first !== undefined) processedMutationIds.delete(first);
        }
      }

      const startPath = mutationStartPathMap.get(mutation) || getPath();
      recordAction(startPath, {
        label: typeof meta.activity === 'string' ? meta.activity : 'Completed action',
        status: 'success',
        entity: meta.entity as { type: string; id?: string; name?: string } | undefined,
      });
    },
  });
}
